import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Heart, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { JacKeyIcon } from '@/components/wizard/JacKeyIcon';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import { supabase } from '@/integrations/supabase/client';

const copy = {
  en: {
    greeting: 'Hey! 💚 Moved by this message?',
    reply: 'Reply to the sender',
    send: 'Send someone you care about a message',
  },
  es: {
    greeting: '¡Hola! 💚 ¿Te conmovió este mensaje?',
    reply: 'Responder al remitente',
    send: 'Envía un mensaje a alguien que te importa',
  },
  fr: {
    greeting: 'Hey ! 💚 Touché(e) par ce message ?',
    reply: 'Répondre à l\'expéditeur',
    send: 'Envoyez un message à quelqu\'un que vous aimez',
  },
  pt: {
    greeting: 'Ei! 💚 Tocado(a) por esta mensagem?',
    reply: 'Responder ao remetente',
    send: 'Envie uma mensagem para alguém que você ama',
  },
  ar: {
    greeting: 'مرحباً! 💚 هل أثرت فيك هذه الرسالة؟',
    reply: 'الرد على المرسل',
    send: 'أرسل رسالة لشخص تهتم به',
  },
  ja: {
    greeting: 'こんにちは！💚 このメッセージに感動しましたか？',
    reply: '送信者に返信する',
    send: '大切な人にメッセージを送る',
  },
  ko: {
    greeting: '안녕하세요! 💚 이 메시지에 감동받으셨나요?',
    reply: '보낸 사람에게 답장하기',
    send: '소중한 사람에게 메시지 보내기',
  },
  id: {
    greeting: 'Hai! 💚 Tersentuh oleh pesan ini?',
    reply: 'Balas ke pengirim',
    send: 'Kirim pesan ke seseorang yang kamu sayangi',
  },

  hi: {
    greeting: 'अरे! 💚 इस संदेश से प्रभावित हुए?',
    reply: 'प्रेषक को जवाब दें',
    send: 'किसी ऐसे व्यक्ति को संदेश भेजें जिसकी आप परवाह करते हैं',
  },
};

interface JacKeyPostReadBubbleProps {
  /** ID selector or ref callback to scroll to the reply section */
  onReply: () => void;
  source?: string;
}

export function JacKeyPostReadBubble({ onReply, source = 'announcement_view' }: JacKeyPostReadBubbleProps) {
  const { language } = useLanguage();
  const t = lc(copy, language);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const tracked = useRef(false);

  // Show bubble when user scrolls near the bottom of the message content
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !dismissed) {
          setVisible(true);
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [dismissed]);

  // Track impression
  useEffect(() => {
    if (visible && !tracked.current) {
      tracked.current = true;
      supabase.from('events' as any).insert({
        event_name: 'jackey_post_read_bubble_shown',
        properties: { source },
      }).then(() => {});
    }
  }, [visible, source]);

  const handleReply = () => {
    supabase.from('events' as any).insert({
      event_name: 'jackey_post_read_reply_clicked',
      properties: { source },
    }).then(() => {});
    setDismissed(true);
    setVisible(false);
    onReply();
  };

  const handleSend = () => {
    supabase.from('events' as any).insert({
      event_name: 'jackey_post_read_send_clicked',
      properties: { source },
    }).then(() => {});
    setDismissed(true);
    setVisible(false);
    window.location.href = '/?ref=recipient_jackey';
  };

  const handleDismiss = () => {
    setDismissed(true);
    setVisible(false);
  };

  return (
    <>
      {/* Invisible sentinel placed at the end of message content */}
      <div ref={sentinelRef} className="w-full h-1" aria-hidden="true" />

      <AnimatePresence>
        {visible && !dismissed && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
            className="fixed bottom-20 right-4 left-4 sm:left-auto sm:right-6 sm:max-w-sm z-50"
          >
            <div className="relative glass rounded-2xl border border-primary/20 shadow-2xl shadow-primary/10 p-4">
              {/* Dismiss */}
              <button
                onClick={handleDismiss}
                className="absolute top-2 right-2 p-1 rounded-full hover:bg-muted/50 text-muted-foreground transition-colors"
                aria-label="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>

              {/* JacKey avatar + greeting */}
              <div className="flex items-start gap-3 mb-3">
                <motion.div
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <JacKeyIcon size="sm" />
                </motion.div>
                <p className="text-sm font-medium text-foreground leading-snug pt-0.5">
                  {t.greeting}
                </p>
              </div>

              {/* Action buttons */}
              <div className="flex flex-col gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full justify-start gap-2 rounded-xl text-left"
                  onClick={handleReply}
                >
                  <MessageCircle className="h-4 w-4 text-primary shrink-0" />
                  <span className="truncate">{t.reply}</span>
                </Button>
                <Button
                  size="sm"
                  className="w-full justify-start gap-2 rounded-xl text-left"
                  onClick={handleSend}
                >
                  <Heart className="h-4 w-4 shrink-0" />
                  <span className="truncate">{t.send}</span>
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
